import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { siteConfig } from "@latam-energy/content";
import { absoluteUrl, generatePageMetadata } from "@latam-energy/seo";
import { siteUrl } from "@/lib/utils";

type SeoInput = {
  title: string;
  description: string;
  pathname?: string;
  noIndex?: boolean;
};

const defaultSeo = {
  title: siteConfig.defaultSeoTitle,
  description: siteConfig.defaultSeoDescription,
  pathname: "/"
};

export function usePageSeo(input?: SeoInput) {
  const location = useLocation();
  const seo = input ?? { ...defaultSeo, pathname: location.pathname };

  useEffect(() => {
    const metadata = generatePageMetadata({
      ...seo,
      pathname: seo.pathname ?? location.pathname,
      siteUrl: siteUrl()
    });

    document.documentElement.lang = "es-PE";
    document.title = metadata.title;
    setMeta("name", "description", metadata.description);
    setMeta("name", "robots", metadata.robots.index ? "index,follow" : "noindex,nofollow");
    setMeta("property", "og:title", metadata.openGraph.title);
    setMeta("property", "og:description", metadata.openGraph.description);
    setMeta("property", "og:type", metadata.openGraph.type);
    setMeta("property", "og:site_name", metadata.openGraph.siteName);
    setMeta("property", "og:locale", metadata.openGraph.locale);
    setMeta("property", "og:url", metadata.openGraph.url);
    setMeta("property", "og:image", metadata.openGraph.images[0].url);
    setMeta("name", "twitter:card", metadata.twitter.card);
    setMeta("name", "twitter:title", metadata.twitter.title);
    setMeta("name", "twitter:description", metadata.twitter.description);
    setMeta("name", "twitter:image", metadata.twitter.images[0]);
    setCanonical(metadata.alternates.canonical);
  }, [location.pathname, seo.description, seo.noIndex, seo.pathname, seo.title]);
}

export function defaultMetadata(pathname = "/") {
  return generatePageMetadata({ ...defaultSeo, pathname, siteUrl: siteUrl() });
}

export function canonical(pathname: string) {
  return absoluteUrl(pathname, siteUrl());
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}
